/**
 * Pure feature-name resolution for route-based usage tracking.
 *
 * No React / router / analytics imports so it stays trivially unit-testable and
 * transform-free under Jest.
 */

/**
 * Maps a top-level app section (the segment after `/app/`, or the first segment
 * for standalone authed routes) to a human-readable feature name. These names are
 * what the atEaseAI usage webhook sends as `feature_name`; they are matched to the
 * product catalog on ingest (ignoring case and separators), so keep them aligned
 * with the catalog features you want them to count toward.
 */
export const FEATURE_MAP = {
  dashboard: 'Dashboard',
  accounts: 'Accounts',
  communications: 'Communications',
  'ai-actions': 'AI Actions',
  onboarding: 'Onboarding',
  'tasks-board': 'Tasks',
  meetings: 'Meetings',
  opportunities: 'Opportunities',
  settings: 'Settings',
  'business-profile': 'Business Profile',
  analytics: 'Analytics',
  chat: 'Caleb Chat',
  refer: 'Refer',
  'knowledge-base': 'Knowledge Base',
  'my-integrations': 'My Integrations',
  'api-docs': 'API Docs',
};

/**
 * Resolve the feature name for a pathname, or null if it isn't a trackable
 * feature area (public/marketing/auth pages, or the bare `/app` redirect).
 *
 * @param {string} pathname
 * @returns {string|null}
 */
export const resolveFeatureName = (pathname) => {
  const segments = (pathname || '').split('/').filter(Boolean);
  if (segments.length === 0) return null;

  // `/app/<section>/...` → the section; standalone authed routes → first segment.
  const section = segments[0] === 'app' ? segments[1] : segments[0];
  if (!section) return null; // bare `/app`

  // hasOwnProperty (not `in`) so inherited keys like `toString` never match.
  if (Object.prototype.hasOwnProperty.call(FEATURE_MAP, section)) return FEATURE_MAP[section];

  // Only fall back for authenticated areas; ignore public marketing routes.
  if (segments[0] !== 'app') return null;
  // Title-case the raw slug as a best-effort name for any new/unmapped section.
  return section
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};
